import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { TenantRelation, TenantRelationWithDisable } from '@shared/models/tenant-relation';
import { TenantRelationAssignment } from '@shared/models/tenant-relation-assignment';
import { TenantRelationsApiService } from '@modules/bcm/@shared/services';
import {
    MAT_DIALOG_DATA,
    MatDialogRef
} from '@angular/material/dialog';
import { AppNotificationService } from '@core/services/app-notification.service';
import { hours, minutes } from '@shared/constants/date';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { U2bDateValidators } from '@shared/validators/date/date-validators';
import { Season } from '@shared/models/seasons';
import { SeasonApiService } from '@bcmApiServices/seasons.api-service';
import { PayableOptionId } from '@bcmApiServices/product-payable-option.api-service';
import { isValidDate } from '@core/functions/is-valid-date';
import { addDays, addHours, addMonths, addWeeks, addYears, endOfMonth, subMinutes } from '@core/date.facade';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { Product } from '@shared/models/product';
import { PayableOption } from '@shared/models/payable-option';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { Boat } from '@shared/models/boat';
import { Person } from '@shared/models/person';
import { Company } from '@shared/models/company';
import { PriceRuleRequirements } from '@shared/models/price-rule-requirements';
import { DynamicPriceRulesEntityId } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-entity-id';
import { TolgeeService } from '@core/translation/tolgee.service';
import { DynamicPriceRulesQueryOperator } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-query-operator';

@Component({
    selector: 'dialog-tenant-relation',
    templateUrl: './get-tenant-relation-data-dialog.component.html',
    standalone: false
})
export class GetTenantRelationDataDialogComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject();

    @Input()
    tenantRelationAssignment: TenantRelationAssignment;

    protected readonly BcmTenantPermission = BcmTenantPermission;

    tenantRelations: TenantRelation[] = [];

    seasons: Season[];

    tenantRelationDatesForm: UntypedFormGroup;

    subscriptionForm: UntypedFormGroup;

    seasonFormControl = new FormControl<Season>(null);

    @Input()
    parentFormGroup: UntypedFormGroup;

    formGroup: UntypedFormGroup;

    productsCostCenterFormControls: FormControl[] = [];

    loading: boolean;

    hours = hours;

    minutes = minutes;

    PayableOptionId = PayableOptionId;

    products: Product[] = [];

    selectedTenantRelations: TenantRelation[] = [];

    /**
     * The products of a selected relation are fetched separately (see `_withProducts`). Saving
     * before they arrived, or after the fetch failed, would drop the whole subscription block from
     * the payload — while the backend still flags the assignment as a subscription, leaving an
     * "Abo ohne Startdatum" the scheduler never bills. So both states block saving. (#6928)
     */
    productsLoading = false;

    productsFailed = false;

    payableOption?: PayableOption;

    /** Expose enum so the template can reference it via priceRuleRequirements.requires(DynamicPriceRulesEntityId.Boat). */
    DynamicPriceRulesEntityId = DynamicPriceRulesEntityId;

    /**
     * Derived from the products of the currently selected tenant relations.
     * Re-evaluated each time the tenant relation selection changes.
     * Drives which entity selectors (boat, berth, ...) are conditionally rendered.
     */
    priceRuleRequirements = new PriceRuleRequirements([]);

    /**
     * Boats belonging to the person or company the relation is being created for.
     * Passed to form-widget-boat so their boats appear at the top of the autocomplete list.
     * Empty when no person/company context was provided (e.g. bulk operations).
     */
    givenBoats: Boat[] = [];

    /**
     * Relations the person or company already holds. Passed to form-widget-tenant-relation so they
     * are listed first, under their own "Beziehungen von …" group, ahead of the remaining ones.
     * Empty when no person/company context was provided (e.g. bulk operations).
     */
    givenTenantRelations: TenantRelation[] = [];

    /**
     * Display name of the person or company the relation is being created for. Doubles as the group
     * label in both the relation and the boat autocomplete — the relation widget only renders its
     * top list when this is set.
     */
    personOrCompanyName: string;

    multipleSelectable = false;
    hasSeasonsPermission = false;

    /**
     * Caches the full tenant relation (incl. products) per id. The `/select` list that feeds the
     * relation widget is slim and omits products (perf(6622)). We load the full relation once per
     * id and reuse it, so reselecting an already-loaded relation stays instant and loop-free.
     */
    private readonly _fullRelationCache = new Map<number, TenantRelation>();

    disableConditionCallback = (selectedItems: TenantRelationWithDisable[], allItems: TenantRelationWithDisable[]) => {
        allItems.forEach((currentItem) => {
            const foundElement = selectedItems.find((selectedElement) => selectedElement.payableOption?.id);
            const isItemSelected = selectedItems.some((item) => item.id === currentItem.id);
            // A relation without a payable option carries no billing interval of its own, so it can
            // never conflict with the locked one - `foundElement` skips those for the same reason,
            // as does the interval the subscription form derives. Requiring an id on both sides
            // also stops the message below from dereferencing a null payableOption.
            const hasDifferentPayableOption = !!foundElement?.payableOption?.id
                && !!currentItem.payableOption?.id
                && currentItem.payableOption.id !== foundElement.payableOption.id;

            currentItem.disabled = isItemSelected || hasDifferentPayableOption;

            if (isItemSelected) {
                currentItem.disabledReason = 'Bereits selektiert.';
            } else if (hasDifferentPayableOption) {
                currentItem.disabledReason = this._tolgee.translate(
                    'bcmTenantRelations.assignmentsTable.payableOptionMismatchDisabledReason',
                    {
                        foundInterval: this._tolgee.translate(foundElement.payableOption.translationKey),
                        currentInterval: this._tolgee.translate(currentItem.payableOption.translationKey),
                    },
                );
            }
        });
    };

    constructor(
        public bcmTenantService: BcmTenantService,
        public dialogRef: MatDialogRef<GetTenantRelationDataDialogComponent>,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: {
            tenantRelationAssignment: TenantRelationAssignment;
            multipleSelectable: boolean;
            /** Pass the person the relation is being created for to scope the boat selector to their boats. */
            person?: Person;
            /** Pass the company the relation is being created for to scope the boat selector to their boats. */
            company?: Company;
        },
        private _formBuilder: UntypedFormBuilder,
        private _tenantRelationsApiService: TenantRelationsApiService,
        private _seasonApiService: SeasonApiService,
        private _appNotificationService: AppNotificationService,
        private _tolgee: TolgeeService,
    ) {
        this.dialogRef.addPanelClass('default-dialog');

        this.tenantRelationAssignment = this.data?.tenantRelationAssignment;
        this.multipleSelectable = this.data?.multipleSelectable || false;

        // Populate the context from whoever the relation is being created for. The boat selector
        // (shown when a product requires boat data) presents their boats first, and the relation
        // autocomplete lists the relations they already hold ahead of all the others — both make
        // selection faster for the common case.
        const person = this.data?.person;
        const company = this.data?.company;
        this.givenBoats = person?.boats || company?.boats || [];
        this.personOrCompanyName = person?.fullName || company?.fullName;
        // Same source the dynamic-price widget uses for its relation top list, so both agree on
        // what counts as a relation the person or company currently holds.
        this.givenTenantRelations = (person?.activeTenantRelationAssignments
            || company?.activeTenantRelationAssignments
            || [])
            .map((tenantRelationAssignment) => tenantRelationAssignment.tenantRelation);

        this.createMandatoryForm();
        this.createOptionalForm();
    }

    ngOnInit(): void {
        this.loading = true;

        this.hasSeasonsPermission = this.bcmTenantService.checkPermission(BcmTenantPermission.SEASONS);

        if (this.parentFormGroup) {
            this.parentFormGroup.addControl('tenantRelationAssignmentForm', this.formGroup);
        }

        this._tenantRelationsApiService
            .getAll()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((tenantRelations) => {
                this.tenantRelations = tenantRelations;
                this.loading = false;
            });

        this._seasonApiService
            .getAllSeasons()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((seasons) => {
                this.seasons = seasons;
            });

        this.seasonFormControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((season) => {
            this.subscriptionForm.patchValue({
                vestingPeriodFrom: season?.startDate || null,
                vestingPeriodUntil: season?.endDate || null
            });
        });

        if (!this.hasSeasonsPermission) {
            this.seasonFormControl.disable({ emitEvent: false });
            this.seasonFormControl.disable({ emitEvent: false });
        }
    }

    ngOnDestroy(): void {
        if (this.parentFormGroup) {
            this.parentFormGroup.removeControl('tenantRelationAssignmentForm');
        }
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    compareSeasons(season1: Season, season2: Season): boolean {
        return season1?.id === season2?.id;
    }

    /**
     * Whether the current selection carries fees, and therefore needs the subscription fields.
     * Read off the relation itself (`hasProducts`, shipped by the slim list endpoint) instead of the
     * loaded products, so the fields show up the moment a relation is picked — the products only
     * add prices and cost centers on top.
     */
    get hasFeeRelation(): boolean {
        return this.selectedTenantRelations.some((tenantRelation) => tenantRelation.hasProducts);
    }

    /**
     * Retries a failed enrichment. It has to call the handler directly: re-picking the same relation
     * produces the same selection key, which the dedupe on the value stream swallows — so without
     * this the failed state could only be left by closing the dialog. (#6928)
     */
    retryProductLoad(): void {
        this._onSelectedTenantRelationsChanged(this._selectedTenantRelationsFromForm());
    }

    /**
     * Tenant relations referenced by `tenantRelation` criteria on the selected products'
     * price rules, split by whether the rule requires the relation to be present (`=`) or
     * absent (`!=`). Used to render a hint box telling the user which relations the
     * person/company must or must not have for the configured price rule(s) to apply.
     * No select — per ticket the selection itself is not made here, only the requirement
     * is surfaced.
     */
    get tenantRelationRuleHints(): { required: TenantRelation[]; excluded: TenantRelation[] } {
        const requiredIds = new Set<number>();
        const excludedIds = new Set<number>();

        for (const product of (this.products || [])) {
            for (const ruleSet of (product.priceRuleMap?.ruleSets || [])) {
                for (const rule of (ruleSet.rules || [])) {
                    if (rule.entityId !== DynamicPriceRulesEntityId.TenantRelation || rule.value == null) {
                        continue;
                    }
                    const id = Number(rule.value);
                    if (rule.operator === DynamicPriceRulesQueryOperator.NotEqual) {
                        excludedIds.add(id);
                    } else {
                        requiredIds.add(id);
                    }
                }
            }
        }

        const byId = (ids: Set<number>) => (this.tenantRelations || []).filter(tr => ids.has(tr.id));
        return {required: byId(requiredIds), excluded: byId(excludedIds)};
    }

    save(): void {
        if (this.productsLoading || this.productsFailed) {
            this._appNotificationService.showError(
                this._tolgee.translate('dialogs.tenantRelationData.productsUnresolved')
            );
            return;
        }

        if (this.tenantRelationDatesForm.invalid || (this.hasFeeRelation && this.subscriptionForm.invalid)) {
            this.tenantRelationDatesForm.markAllAsTouched();
            this.subscriptionForm.markAllAsTouched();
            this._appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }

        let formValueRaw = {
            ...this.tenantRelationDatesForm.getRawValue()
        };

        if (this.hasFeeRelation) {
            formValueRaw = {
                ...formValueRaw,
                ...this.subscriptionForm.getRawValue()
            };
        }

        const costCenters = this.productsCostCenterFormControls.map((control) => control.value);

        // Each form-widget-* component registers its controls under a named sub-group on the
        // nearest parent FormGroup via FormGroupDirective (e.g. form-widget-boat → 'boatForm').
        // We hoist each selected entity to the top level of the close payload so the backend
        // receives a flat req.body.boat (or req.body.berth, ...) rather than having to
        // navigate the nested form structure.
        const boat          = formValueRaw.boatForm?.boat ?? null;
        const berth         = formValueRaw.berthForm?.berth ?? null;
        const winterStorage = formValueRaw.winterStorageForm?.winterStorage ?? null;
        const pier          = formValueRaw.pierForm?.pier ?? null;
        const season        = formValueRaw.seasonForm?.season ?? null;

        this.dialogRef.close({
            ...formValueRaw,
            ...(formValueRaw.tenantRelationForm || {}),
            costCenters,
            boat,
            berth,
            winterStorage,
            pier,
            season
        });
    }

    private createMandatoryForm() {
        this.tenantRelationDatesForm = this._formBuilder.group(
            {
                fromDate: [
                    this.tenantRelationAssignment?.fromDate,
                    [U2bValidators.required(this._tolgee.translate('common.validationMsg.subscriptionStartDateRequired'))]
                ],
                toDate: [this.tenantRelationAssignment?.toDate]
            },
            {
                validators: [U2bDateValidators.dateBeforeOtherDate('fromDate', 'toDate', 'Start', 'Ende')]
            }
        );

        this.tenantRelationDatesForm.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((value) => {
                    const tenantRelations = value?.tenantRelationForm?.tenantRelation
                        ? [value?.tenantRelationForm?.tenantRelation]
                        : value?.tenantRelationForm?.tenantRelations || [];
                    // The chip list mutates its selection array in place, so distinctUntilChanged
                    // below would hold that very array as "previous" and compare it against itself —
                    // every multi-select change after the first was swallowed. Snapshot it. (#6928)
                    return [...tenantRelations];
                }),
                // Only the relation selection feeds the section below — without this the products
                // (and every cost-center control the user already filled in) were rebuilt on every
                // keystroke in a date field, and the enrichment below would refire per keystroke too.
                distinctUntilChanged((previous, current) => this._selectionKey(previous) === this._selectionKey(current))
            )
            .subscribe((tenantRelations: TenantRelation[]) => this._onSelectedTenantRelationsChanged(tenantRelations));
    }

    /**
     * The `/select` list feeding the relation widget is slim and carries no products (perf(6622)),
     * but it does carry `hasProducts` — enough to decide right away whether the subscription section
     * below "Laufzeit der Beziehung" belongs on screen. The products themselves are loaded on demand
     * for prices and cost centers, cached per id, so reselecting stays instant.
     */
    private _onSelectedTenantRelationsChanged(tenantRelations: TenantRelation[]): void {
        this.productsLoading = false;
        this.productsFailed = false;

        if (!tenantRelations?.length) {
            this._applySelectedTenantRelations([]);
            return;
        }

        // Apply the slim selection first so the subscription fields appear immediately, instead of
        // only once the round-trip below returns — or never, if it fails.
        this._applySelectedTenantRelations(tenantRelations);

        const requestedKey = this._selectionKey(tenantRelations);
        this.productsLoading = true;

        forkJoin(tenantRelations.map((tenantRelation) => this._withProducts(tenantRelation)))
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((enrichedTenantRelations) => {
                // Ignore out-of-order responses: rapid re-selection would otherwise let a stale
                // response overwrite the current selection.
                if (this._selectionKey(this._selectedTenantRelationsFromForm()) !== requestedKey) {
                    return;
                }

                this.productsLoading = false;
                // `_withProducts` falls back to the slim relation when the request fails, so a
                // fee-bearing relation without products means exactly that — surface it rather than
                // saving an assignment whose subscription data silently went missing.
                this.productsFailed = enrichedTenantRelations
                    .some((tenantRelation) => tenantRelation.hasProducts && !tenantRelation.products?.length);

                this._applySelectedTenantRelations(enrichedTenantRelations);
                this._writeBackEnrichedSelection(enrichedTenantRelations);
            });
    }

    /** Resolves a relation to one that carries its products, straight from cache where possible. */
    private _withProducts(tenantRelation: TenantRelation): Observable<TenantRelation> {
        if (!tenantRelation?.id || tenantRelation.products?.length) {
            return of(tenantRelation);
        }

        const cached = this._fullRelationCache.get(tenantRelation.id);
        if (cached) {
            return of(cached);
        }

        return this._tenantRelationsApiService
            .getOne(tenantRelation.id)
            .pipe(
                tap((fullTenantRelation) => this._fullRelationCache.set(fullTenantRelation.id, fullTenantRelation)),
                // Enrichment failed → keep the slim relation so the selection is not silently
                // dropped (matches the previous synchronous behaviour).
                catchError(() => of(tenantRelation))
            );
    }

    /**
     * Hands the enriched relations back to the relation widget. It evaluates the dynamic prices off
     * its own copy of the selection (`_evaluateDynamicPrices`), so without this it would keep
     * iterating the slim, product-less one and never resolve a rule price — leaving the "Produkte
     * gefunden" box showing the static price only.
     */
    private _writeBackEnrichedSelection(enrichedTenantRelations: TenantRelation[]): void {
        const tenantRelationForm = this.tenantRelationDatesForm.get('tenantRelationForm');
        const controlName = this.multipleSelectable ? 'tenantRelations' : 'tenantRelation';
        const control = tenantRelationForm?.get(controlName);

        if (!control) {
            return;
        }

        const current: TenantRelation[] = this.multipleSelectable ? (control.value || []) : [control.value];
        const unchanged = current.length === enrichedTenantRelations.length
            && current.every((tenantRelation, index) => tenantRelation === enrichedTenantRelations[index]);

        if (unchanged) {
            return;
        }

        // Emits, so the widget re-evaluates. Our own subscription above dedupes on the relation ids,
        // which do not change here — no feedback loop.
        control.setValue(this.multipleSelectable ? enrichedTenantRelations : enrichedTenantRelations[0]);
    }

    private _applySelectedTenantRelations(tenantRelations: TenantRelation[]): void {
        this.selectedTenantRelations = tenantRelations || [];
        this.payableOption = this.selectedTenantRelations
            .find((relation) => !!relation.payableOption?.id)?.payableOption;

        const products = this.selectedTenantRelations.flatMap((relation) => relation.products || []);

        if (products.length) {
            this.products = products;
            this.productsCostCenterFormControls = products.map((product) => {
                const costCenter = product.costCenters?.length === 1 ? product.costCenters[0] : undefined;
                return new FormControl(costCenter);
            });
            // Derive which entity selectors to show from the products' price rule maps.
            // The template uses priceRuleRequirements.requires(EntityId) to conditionally
            // render the matching form widget and block saving when no value is selected.
            this.priceRuleRequirements = new PriceRuleRequirements(products);
        } else {
            this.products = [];
            this.productsCostCenterFormControls = [];
            // Reset so previously shown selectors are hidden when the relation is cleared.
            this.priceRuleRequirements = new PriceRuleRequirements([]);
        }
    }

    /** Selection identity used for deduping and for discarding out-of-order enrichment responses. */
    private _selectionKey(tenantRelations: TenantRelation[]): string {
        return (tenantRelations || []).map((tenantRelation) => tenantRelation?.id).join(',');
    }

    /** The relations currently selected in the widget — the source the enrichment maps from. */
    private _selectedTenantRelationsFromForm(): TenantRelation[] {
        const value = this.tenantRelationDatesForm.get('tenantRelationForm')?.value;

        return value?.tenantRelation ? [value.tenantRelation] : (value?.tenantRelations || []);
    }

    private createOptionalForm() {
        this.subscriptionForm = this._formBuilder.group(
            {
                periodFromDate: [
                    this.tenantRelationAssignment?.periodFromDate,
                    [U2bValidators.required(this._tolgee.translate('common.validationMsg.relationSubscriptionStartDateRequired'))]
                ],
                periodToDate: [this.tenantRelationAssignment?.periodToDate],
                vestingPeriodFrom: [
                    this.tenantRelationAssignment?.vestingPeriodFrom,
                    [U2bValidators.required(this._tolgee.translate('common.validationMsg.servicePeriodRequired'))]
                ],
                vestingPeriodUntil: [
                    this.tenantRelationAssignment?.vestingPeriodUntil || null,
                    [U2bValidators.required(this._tolgee.translate('common.validationMsg.servicePeriodRequired'))]
                ],
                lastDayOfMonth: [this.tenantRelationAssignment?.lastDayOfMonth || false]
            },
            {
                validators: [
                    U2bDateValidators.dateBeforeOtherDate('periodFromDate', 'periodToDate', 'Abo Start', 'Abo Ende'),
                    U2bDateValidators.dateBeforeOtherDate(
                        'vestingPeriodFrom',
                        'vestingPeriodUntil',
                        'Leistungszeitraum Start',
                        'Leistungszeitraum Ende'
                    )
                ]
            }
        );

        const subscriptionForm = this.subscriptionForm;
        const vestingPeriodFromField = subscriptionForm.get('vestingPeriodFrom');
        const vestingPeriodUntilField = subscriptionForm.get('vestingPeriodUntil');
        const lastDayOfMonthField = subscriptionForm.get('lastDayOfMonth');

        vestingPeriodFromField.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            if (isValidDate(value) && this.payableOption?.id && vestingPeriodUntilField.value == null) {
                let newDate: Date;
                switch (this.payableOption.id) {
                    case PayableOptionId.Yearly:
                        newDate = addYears(value, 1);
                        break;
                    case PayableOptionId.HalfYearly:
                        newDate = addMonths(value, 6);
                        break;
                    case PayableOptionId.QuarterYearly:
                        newDate = addMonths(value, 3);
                        break;
                    case PayableOptionId.Monthly:
                        newDate = addMonths(value, 1);
                        break;
                    case PayableOptionId.Weekly:
                        newDate = addWeeks(value, 1);
                        break;
                    case PayableOptionId.Daily:
                        newDate = addDays(value, 1);
                        break;
                    case PayableOptionId.Hourly:
                        newDate = addHours(value, 1);
                        break;
                }
                vestingPeriodUntilField.patchValue(subMinutes(newDate, 1));
            }
        });

        lastDayOfMonthField.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            if (value) {
                subscriptionForm.get('vestingPeriodUntil').disable();
                subscriptionForm
                    .get('vestingPeriodUntil')
                    .patchValue(
                        endOfMonth(
                            subscriptionForm.get('vestingPeriodUntil').value ||
                                subscriptionForm.get('vestingPeriodFrom').value
                        ),
                        { emitEvent: false }
                    );
            } else {
                subscriptionForm.get('vestingPeriodUntil').enable();
            }
        });
    }
}
